import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { firebase } from "../../firebase";
import { Col, ControlLabel, FormGroup, Row, Modal, HelpBlock } from "react-bootstrap";
import SettingsInput from "./SettingsInput";
import { useForm } from "react-hook-form";
import { environment } from "configs/environment";
import WaitlistSettingsHour from "./WaitlistSettingsHour";
import "./ReservationsSettings.scss";
import { week } from "../../constants/time";
import { updateBusinessSettings, upgradePremiumToPro } from "../../services/profile/profile.service";
import { sendHelpEmail } from "services/help/help.service";
import toastr from "toastr";
import SettingsWrapper from "../../common/ScrollableWrapper/SettingsWrapper";
import Button from "../CustomButton/CustomButton";
import RegularTable from "../../views/RegularTable";
import { ACTIONS, APPOINTMENT_HEADERS, HEADERS } from "../../constants/tables/resources-settings-table";
import cx from "classnames";
import {
  getBusinessResources,
  updateBusinessResource,
  addBusinessResource,
  deleteBusinessResource,
  getBusinessServices,
  addBusinessService,
  updateBusinessService,
  deleteBusinessService
} from "../../services/businesses/businesses.service";
import SweetAlert from "react-bootstrap-sweetalert";
import { deleteMessage } from "../../constants/messages";
import AddResource from "../AddResource/AddResource";
import AddService from "../AddService/AddService";
import { ACTIONS as SERVICE_ACTIONS, HEADERS as SERVICE_HEADERS } from "../../constants/tables/services-settings-table";
import { RESERVATIONS_TYPES } from "constants/reservation";
import { isReservationsBusinessType, getBookingTypeDisplayName, getResourceLabel } from "helpers/businessType.helper";
import { filterActiveServices, formatServiceDuration, sortServices } from "helpers/appointmentService.helper";
import { BeatLoader } from "react-spinners";
import SquareReservationSetup from "../../components/Square/SquareReservationSetup";
import SquareHeader from "components/Square/SquareHeader";
import SquareAppearanceEditModal from "components/Square/SquareAppearanceEditModal";
import { PREMIUM_TO_PRO, SNIPPET_TYPES } from "../../constants/square";
import { useSelector } from "react-redux";
import { createSnippet } from "services/square/square.service";
import WebsiteInstructionsModal from "components/WebsiteInstructionsModal/WebsiteInstructionsModal";
import { STRIPE_UPGRADE_ERROR } from "./SquareSnippetsSettings";
import StripeUpgradeErrorContent from "./StripeUpgradeErrorContent";
import UpgradeSquare from "components/Upgrade/UpgradeSquare";
import { DateTime } from "luxon";
import SpecificDateCalendar from "components/SpecificDates/SpecificDateCalendar";
// Lazy: the editor pulls in TipTap and its ProseMirror core, which is the largest
// dependency on this page and is only reachable behind a button most owners of a
// settings page never press.
const LandingPageEditor = lazy(() => import("components/LandingPageEditor/LandingPageEditor"));
import AddLocationForGoogleModal from "components/AddLocationForGoogleModal/AddLocationForGoogleModal";
import CustomButton from "../CustomButton/CustomButton";
import { useMediaQuery } from "hooks/useMediaQuery";

const ReservationSettings = ({ settings, business, user, plan, location, ...props }) => {
  const { register, handleSubmit, errors, getValues } = useForm();
  const history = useHistory();
  const [form, setForm] = useState({
    enableReservations: false,
    selfBooking: false,
    reserveWithGoogle: false,
    reservationPolicy: "",
    hours: []
  });

  let params = new URLSearchParams(location.search);
  let startWithPurchase = false;
  let startWithResourceSetup = false;
  if (params.get("step") === "activate") {
    startWithPurchase = true;
  } else if (params.get("step") === "paymentSuccess") {
    startWithResourceSetup = true;
  }

  const [bookLink, setBookLink] = useState("");
  const [bookLinkCopied, setBookLinkCopied] = useState(false);
  const [setupResourcesModal, setSetupResourcesModal] = useState(startWithResourceSetup);
  const [confirmationModal, setConfirmationModal] = useState({
    show: false,
    type: null
  });
  const [resources, setResources] = useState([]);
  const [showDeleteMessage, setShowDeleteMessage] = useState(false);
  const [selectedResource, setSelectedResource] = useState(null);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showDeleteServiceMessage, setShowDeleteServiceMessage] = useState(false);
  const [showSpecificDatesModal, setShowSpecificDatesModal] = useState(false);
  const [showLandingPageEditor, setShowLandingPageEditor] = useState(false);
  const [customSettingsUpdatedMessage, setCustomSettingsUpdatedMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [resourcesLoading, setResourcesLoading] = useState(false);
  const [maxResourcesCount, setMaxResourcesCount] = useState(null);
  const [shouldSubmitResources, setShouldSubmitResources] = useState(false);
  const [onlineReservations, setOnlineReservationsModal] = useState(false);
  const [isOnlineReservationsModalFromGoogle, setIsOnlineReservationsModalFromGoogle] = useState(false);
  const [editSnippetModalConfig, setEditSnippetModalConfig] = useState(null);
  const [snippetContent, setSnippetContent] = useState(null);
  const [isShowWebsiteInstructionsModal, setIsShowWebsiteInstructionsModal] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState(false);
  const [purchaseModal, setPurchaseModal] = useState(startWithPurchase);
  const [upgradeErrorModal, setUpgradeErrorModal] = useState(false);
  const [showGoogleLocationModal, setShowGoogleLocationModal] = useState({ show: false, type: null });
  const { account } = useSelector(state => state.account || {});
  const deleteResourceMessage = deleteMessage("resource");
  const deleteServiceMessage = deleteMessage("service");
  const squareSelector = useSelector(state => state.square);
  const { squareInfo } = squareSelector;
  // The Admin layout fetches /square/info once per hard load and dispatches it into
  // the store; this page reads it from there. Deriving "loading" from presence
  // rather than fetching again is what keeps visiting this tab at one request —
  // previously this effect fired a second identical GET on every mount.
  const squareLoading = !squareInfo;
  const [coverLimitOptions, setCoverLimitOptions] = useState([]);
  const isMobile = useMediaQuery("(max-width: 768px)");

  // The last selfBooking value loaded from or written to the server. The
  // auto-save effect compares against it so the initial hydration — which changes
  // form.selfBooking from the component's pre-load default to the saved value —
  // never reads as a change, while every later one does.
  const savedSelfBooking = useRef(null);

  // Flipping the online-booking switch persists immediately; the Save button is no
  // longer required for this setting. Watching the value rather than the toggle
  // handler also covers the programmatic flips (setup modal cancel/complete, the
  // enable-reservations cascade), so the form and the server can't drift apart.
  // Runs in an effect — post-commit — because onSubmit reads `form` from closure
  // and would otherwise see the pre-toggle value. Declared before the hydration
  // effect below on purpose: on mount it must run while `savedSelfBooking` is
  // still null, i.e. before hydration seeds it.
  useEffect(() => {
    if (savedSelfBooking.current === null) return;
    const selfBooking = form.selfBooking || false;
    if (selfBooking === savedSelfBooking.current) return;
    savedSelfBooking.current = selfBooking;
    // Same mechanism the Reserve-with-Google flows already use: click the
    // wrapper's submit button so react-hook-form gathers the registered inputs
    // and runs the exact save path a manual Save runs.
    document.getElementById("reservations-settings-save-btn")?.click();
  }, [form.selfBooking]);

  useEffect(() => {
    if (settings) {
      setForm({
        enableReservations: settings?.enableReservations,
        allowBookingLargerResource: settings?.allowBookingLargerResource || false,
        selfBooking: settings?.selfBooking,
        reserveWithGoogle: settings?.reserveWithGoogle,
        leadTimeValue: settings?.leadTimeValue || 30,
        leadTimeUnits: settings?.leadTimeUnits || "minutes",
        scheduleWindowValue: settings?.scheduleWindowValue || 30,
        scheduleWindowUnits: settings?.scheduleWindowUnits || "days",
        coverFlowLimit: settings?.coverFlowLimit || "",
        reservationPolicy: settings?.reservationPolicy || "",
        // New setting, so most businesses have no value for it — unset reads as "show
        // the map", the behaviour they have today. `||` rather than `!== false` for
        // that reason: this one defaults off, unlike the appointments toggles below.
        hideMap: settings?.hideMap || false,
        hours: settings?.reservations?.hours || [],
        // Appointments booking-flow settings (plan §3.5). Defaults here must match
        // getAppointmentSettings() on the server, which stands in until this is saved.
        appointmentStepOrder: settings?.appointments?.stepOrder || "service-first",
        appointmentShowFirstAvailable: settings?.appointments?.showFirstAvailable !== false,
        appointmentShowResourcePhotos: settings?.appointments?.showResourcePhotos !== false,
        appointmentSlotInterval: settings?.appointments?.slotIntervalMinutes || 15,
        // What this business calls a bookable resource. Seeded from the account type
        // so a salon starts on "Stylist" and a bowling alley on "Lane"; account.type is
        // free-form, so a miss just leaves these blank and the guest sees noun-free copy.
        // `??` so a saved-blank label stays blank instead of the preset repopulating it;
        // the preset only seeds a field that has never been written.
        appointmentResourceLabelSingular:
          settings?.appointments?.resourceLabelSingular ?? getResourceLabel(account?.type)?.singular ?? "",
        appointmentResourceLabelPlural:
          settings?.appointments?.resourceLabelPlural ?? getResourceLabel(account?.type)?.plural ?? "",
        // Landing page (plan §17). Off by default, and the content survives the switch
        // being turned off — disabling hides the page, it does not destroy the draft.
        landingPageEnabled: !!settings?.appointments?.landingPage?.enabled,
        landingPageContent: settings?.appointments?.landingPage?.content || ""
      });

      savedSelfBooking.current = settings?.selfBooking || false;

      let coverLimits = [{ label: "No Limit", value: "" }];
      for (let i = 1; i < 100; i++) {
        coverLimits.push({ value: `${i}`, label: `${i}` });
      }
      setCoverLimitOptions(coverLimits);
      // Reservations and appointments share one public route now — /book/:id decides
      // the flow server-side, on account.type (plan §8/§11 route consolidation).
      setBookLink(`${environment.waitlistUrl}book/${business.businessId}`);
    }
    // eslint-disable-next-line
  }, [business.businessId]);

  const hideInfoAlert = () => {
    setShowDeleteMessage(false);
  };

  const fetchSquareResources = async (withLoading = true) => {
    setLoading(withLoading);
    try {
      if (!business.businessId) throw new Error("Business id is required");
      const reslt = await getBusinessResources(business.businessId);
      if (!reslt || !reslt.docs) throw new Error("Something went wrong, when fetch resources");
      const resorcs = reslt?.docs
        .map(r => r.data())
        .filter(r => r.reservationsType === "reservations")
        .sort((a, b) => a.maxPartySize - b.maxPartySize);
      const maxRsrcCount = Math.max(...resorcs.map(r => r.maxPartySize), 0) || 0;
      if (maxRsrcCount) {
        setResources(resorcs);
        setMaxResourcesCount(maxRsrcCount);
      } else {
        setResources(null);
        setMaxResourcesCount(null);
      }
    } catch (e) {
      console.error(e);
      setResources(null);
      setMaxResourcesCount(null);
    }
    setLoading(false);
  };

  async function loadBusinessResources() {
    if (business.businessId) {
      const resources = await getBusinessResources(business.businessId);
      if (resources && resources.docs.length) {
        const mappedResources = resources.docs
          .map(br => ({
            ...br.data(),
            id: br.id
          }))
          .filter(r => !r.reservationsType || r.reservationsType === "appointments");
        setResources(mappedResources);
      } else {
        setResources([]);
      }
    }
  }

  async function loadBusinessServices() {
    if (!business.businessId) {
      return;
    }
    const snapshot = await getBusinessServices(business.businessId, business.accountId);
    const mappedServices = snapshot?.docs?.map(doc => ({ ...doc.data(), id: doc.id })) || [];
    setServices(sortServices(mappedServices));
  }

  useEffect(() => {
    setResources(null);
    if (isNotTL() && isRestaurantReservations()) {
      fetchSquareResources();
      return;
    }
    loadBusinessResources();
    if (showAppointmentsSetup()) {
      loadBusinessServices();
    }
    // account?.type drives isRestaurantReservations() above, and account can load
    // asynchronously after mount — re-run once it arrives.
    // eslint-disable-next-line
  }, [business.businessId, form.selfBooking, account]);

  const onAddResource = () => {
    setShowResourceModal(true);
  };

  const getReservationsType = () => {
    if (isReservationsBusinessType(account?.type)) {
      return RESERVATIONS_TYPES.RESERVATIONS;
    }
    return RESERVATIONS_TYPES.APPOINTMENTS;
  };

  const onSpecificDates = () => {
    setShowSpecificDatesModal(true);
  };

  // The business's own word for a bookable resource, for use in this page's copy.
  // Falls back to the neutral internal noun, which is what the guest-facing flow does
  // too — a resource can be a person, a room, or a lane.
  const resourceNoun = (plural = false) => {
    const label = plural ? form.appointmentResourceLabelPlural : form.appointmentResourceLabelSingular;
    return (label || "").trim() || (plural ? "Resources" : "Resource");
  };

  // "a stylist" / "an artist" — good enough for the labels owners actually type.
  const aResourceNoun = () => {
    const noun = resourceNoun().toLowerCase();
    return `${/^[aeiou]/.test(noun) ? "an" : "a"} ${noun}`;
  };

  const onGetResourceServiceNames = resource => {
    const names = (resource.serviceIds || [])
      .map(id => services.find(service => service.id === id)?.name)
      .filter(Boolean);
    // A plain string renders as text, an array renders as tags — both are fine here.
    return names.length ? names : "None";
  };

  // Key order is column order — RegularTable renders Object.keys(row) — so each shape
  // has to line up with its own header list (HEADERS / APPOINTMENT_HEADERS).
  const onGetResourcesForTable = () => {
    if (resources.length > 0) {
      return resources.map(resource => {
        if (showAppointmentsSetup()) {
          return {
            name: resource.name,
            services: onGetResourceServiceNames(resource)
          };
        }

        return {
          name: resource.name,
          notificationText: resource.notificationText || ""
        };
      });
    }
  };

  const onSelectResourceForEdit = resource => {
    setSelectedResource(onGetResource(resource));
    setShowResourceModal(true);
  };

  const onShowDeleteResourceMessage = resource => {
    setSelectedResource(onGetResource(resource));
    setShowDeleteMessage(true);
  };

  const onGetResource = resource => {
    return resources.find(r => r.name === resource.name);
  };

  const onHideDeleteAction = () => {
    return false;
  };

  const onDeleteResource = async () => {
    setLoading(true);
    await deleteBusinessResource(business.businessId, selectedResource.id, business.accountId);
    toastr.error("Resource deleted");
    await loadBusinessResources();
    setLoading(false);
    setShowDeleteMessage(false);
    setSelectedResource(null);
  };

  // `clearedFields` names optional fields the modal emptied out; a new document has
  // nothing to clear, and FieldValue.delete() is invalid inside add() anyway.
  const onEditResource = async (resource, clearedFields = []) => {
    const r = {
      ...resource,
      minDuration: resource.minDuration * 1,
      maxReservations: resource.maxReservations * 1
    };
    if (!r.maxDuration) {
      r.maxDuration = 180;
    }
    setLoading(true);
    if (!selectedResource) {
      await addBusinessResource(business.businessId, r);
    } else {
      await updateBusinessResource(business.businessId, selectedResource.id, r, clearedFields);
    }
    await loadBusinessResources();
    toastr.success("Resource updated");
    setShowResourceModal(false);
    setLoading(false);
    setSelectedResource(null);
  };

  const onCloseModal = () => {
    setSelectedResource(null);
    setShowResourceModal(false);
  };

  const onAddService = () => {
    setSelectedService(null);
    setShowServiceModal(true);
  };

  // RegularTable hands the rendered row back to the action callbacks, so each row
  // carries the service id (hidden as a column) to look the real document up.
  const onGetService = row => services.find(service => service.id === row.id);

  const onGetServicesForTable = () =>
    services.map(service => ({
      id: service.id,
      name: service.name,
      duration: formatServiceDuration(service.duration),
      price: typeof service.price === "number" ? `$${service.price.toFixed(2)}` : "—",
      status: service.active === false ? "Not bookable" : "Bookable"
    }));

  const onHideServiceColumn = (_row, key) => key === "id";

  const onSelectServiceForEdit = row => {
    setSelectedService(onGetService(row));
    setShowServiceModal(true);
  };

  const onShowDeleteServiceMessage = row => {
    setSelectedService(onGetService(row));
    setShowDeleteServiceMessage(true);
  };

  const onEditService = async service => {
    setLoading(true);
    try {
      if (selectedService) {
        await updateBusinessService(business.businessId, selectedService.id, service, business.accountId);
      } else {
        await addBusinessService(business.businessId, service, business.accountId);
      }
      await loadBusinessServices();
      toastr.success(selectedService ? "Service updated" : "Service added");
      setShowServiceModal(false);
      setSelectedService(null);
    } catch (e) {
      console.error(e);
      toastr.error("Something went wrong saving the service");
    }
    setLoading(false);
  };

  const onDeleteService = async () => {
    setLoading(true);
    try {
      // Also detaches the service from any resource that offers it.
      await deleteBusinessService(business.businessId, selectedService.id);
      await Promise.all([loadBusinessServices(), loadBusinessResources()]);
      toastr.error("Service deleted");
    } catch (e) {
      console.error(e);
      toastr.error("Something went wrong deleting the service");
    }
    setLoading(false);
    setShowDeleteServiceMessage(false);
    setSelectedService(null);
  };

  const onCloseServiceModal = () => {
    setSelectedService(null);
    setShowServiceModal(false);
  };

  const onCloseSpecificDatesModal = () => {
    setShowSpecificDatesModal(false);
  };

  const onCopyLink = () => {
    setBookLinkCopied(true);
    const el = document.createElement("textarea");
    el.value = bookLink;
    document.body.appendChild(el);
    el.select();
    document.execCommand("copy");
    document.body.removeChild(el);
    toastr.success(`Copied To Clipboard`);
  };

  const onSubmit = async data => {
    const settingsToUpdate = {
      enableReservations: form.enableReservations,
      allowBookingLargerResource: form.allowBookingLargerResource,
      selfBooking: form.enableReservations ? form.selfBooking : false,
      reserveWithGoogle:
        form.enableReservations && form.reserveWithGoogle !== undefined ? form.reserveWithGoogle : false,
      leadTimeValue: form.leadTimeValue,
      leadTimeUnits: form.leadTimeUnits,
      scheduleWindowUnits: form.scheduleWindowUnits,
      scheduleWindowValue: form.scheduleWindowValue,
      coverFlowLimit: form.coverFlowLimit,
      // Applies to both public booking flows — /book and /appointments — so it stays a
      // flat setting rather than living under `settings.appointments`.
      hideMap: !!form.hideMap,
      reservations: {
        hours: form.hours
      }
    };

    if (showAppointmentsSetup()) {
      settingsToUpdate.appointments = {
        stepOrder: form.appointmentStepOrder,
        showFirstAvailable: !!form.appointmentShowFirstAvailable,
        showResourcePhotos: !!form.appointmentShowResourcePhotos,
        slotIntervalMinutes: Number(form.appointmentSlotInterval) || 15,
        resourceLabelSingular: (form.appointmentResourceLabelSingular || "").trim(),
        resourceLabelPlural: (form.appointmentResourceLabelPlural || "").trim(),
        // Written here as well as by the editor's own service call. The merge would
        // preserve an omitted key anyway, but this page rebuilds the whole
        // `appointments` object, so saying so makes the behaviour deliberate rather
        // than incidental (plan §17.2).
        landingPage: {
          enabled: !!form.landingPageEnabled,
          content: form.landingPageContent || ""
        }
      };
    }

    // Only include reservationPolicy if it's not empty, otherwise omit it to trigger deletion
    if (form.reservationPolicy && form.reservationPolicy.trim() !== "") {
      settingsToUpdate.reservationPolicy = form.reservationPolicy;
    } else {
      // Use null to explicitly delete the field from Firestore
      settingsToUpdate.reservationPolicy = firebase.firestore.FieldValue.delete();
    }

    await updateBusinessSettings(business.businessId, {
      ...settingsToUpdate,
      ...data
    });
    if (isRestaurantReservations()) {
      setShouldSubmitResources(true);
    } else {
      toastr.success(`Settings updated`);
    }
  };

  const showOnlineReservationsModal = () => {
    setOnlineReservationsModal(true);
  };

  const hideOnlineReservationModal = () => {
    setOnlineReservationsModal(false);
  };
  const cancelSetupOnlineReservation = () => {
    hideOnlineReservationModal();
    setForm({ ...form, selfBooking: false, reserveWithGoogle: false });
  };

  const toggleChecked = event => {
    setForm({ ...form, [event.target.name]: event.target.checked });
  };
  const isNotTL = () => {
    return account.id !== "xj5spEZSQb2wd0nFRKY6";
  };

  const showOnlineReservation = () => {
    return isRestaurantReservations() || !isNotTL();
  };

  // Appointments settings are for non-restaurant/bar accounts (plan §4). TL is
  // excluded here the same way it is from the rest of the booking UI.
  const showAppointmentsSetup = () => {
    return !isRestaurantReservations() && isNotTL();
  };

  // Services and the rest of the appointments configuration only appear once online
  // booking is on — `selfBooking` is the appointments gate (plan §8), not a new flag.
  const showAppointmentsSettings = () => {
    return showAppointmentsSetup() && form.selfBooking;
  };

  // The online-booking switch itself. Restaurants and TL keep the behaviour they
  // have today; appointment businesses need it because it gates the sections above.
  const showSelfBookingToggle = () => {
    return showOnlineReservation() || showAppointmentsSetup();
  };

  const showRwGSwitch = () => {
    return isRestaurantReservations() && isNotTL();
  };

  // `enableReservations` is deprecated everywhere except TL, which still toggles it
  // both ways. Everyone else only sees the switch while it is off, so a business that
  // turned booking off can turn it back on but cannot turn it off again. This reads the
  // saved value, not `form`, so the switch stays on screen — and a mis-click stays
  // undoable — until the change is actually saved.
  const showEnableReservationsToggle = () => {
    return !isNotTL() || !settings?.enableReservations;
  };

  // The map lives on both public booking flows, so restaurants configure it too —
  // appointments settings just happen to be where the rest of the flow options are.
  const showHideMapToggle = () => {
    return form.selfBooking && (isRestaurantReservations() || showAppointmentsSettings());
  };

  // "Guests choose first" only exists when the guest has a real choice on both
  // lists — with fewer than two active services or two resources, one of the two
  // steps is a single option and the order is moot. Active services only, matching
  // the list the guest actually picks from; `resources` is already the appointment
  // list for a non-restaurant business.
  const showStepOrderSelect = () => filterActiveServices(services).length >= 2 && (resources?.length || 0) >= 2;

  const handleBookingLargerResourceToggle = event => {
    toggleChecked(event);
    if (event.target.checked) {
      setForm({ ...form, allowBookingLargerResource: true });
    } else {
      setForm({ ...form, allowBookingLargerResource: false });
    }
  };

  const handleOnlineReservationToggle = event => {
    toggleChecked(event);
    if (showAppointmentsSetup()) {
      // Appointments have no Reserve with Google and no Square table setup (plan
      // §0), so the switch is just a switch here.
      return;
    }
    if (event.target.checked && isNotTL()) {
      setForm({ ...form, reserveWithGoogle: true });
      showOnlineReservationsModal();
      setIsOnlineReservationsModalFromGoogle(false);
    }
    if (!event.target.checked) {
      setForm(prev => ({ ...prev, reserveWithGoogle: false }));
    }
  };

  const handleReserveWithGoogleToggle = event => {
    toggleChecked(event);
    if (event.target.checked && isNotTL() && !form.selfBooking) {
      setForm({ ...form, selfBooking: true });
      setIsOnlineReservationsModalFromGoogle(true);
      showOnlineReservationsModal();
    }
  };

  const handleEnableReservationToggle = event => {
    if (!event.target.checked) {
      setForm({ ...form, selfBooking: false, enableReservations: false, reserveWithGoogle: false });
    } else {
      toggleChecked(event);
    }
  };

  const showUpgradeModal = () => {
    setUpgradeModal(true);
  };

  const showPurchaseModal = () => {
    setPurchaseModal(true);
  };

  const notifySupportOfReservationsInterest = async () => {
    hideOnlineReservationModal();
    setForm({ ...form, selfBooking: false, reserveWithGoogle: false });
    await sendHelpEmail(
      `I'm interested in Online ${getBookingTypeDisplayName(account?.type)}. Please help me upgrade to Waitly Professional.`
    );
    window.open("https://support.apple.com/en-us/HT204939", "_blank");
  };

  const checkIfAddressForGoogleReservationSetted = () => {
    const address = business?.address || {};

    const isAddressForGoogleReservationSetted = Boolean(
      address.address1 &&
      address.state &&
      address.city &&
      address.postal &&
      address.country &&
      address.geo &&
      address.geo?.lat &&
      address.geo?.lng
    );

    return isAddressForGoogleReservationSetted;
  };

  const showSetupAddressModal = () => {
    hideOnlineReservationModal();

    const isAddressForGoogleReservationSetted = checkIfAddressForGoogleReservationSetted();

    if (isAddressForGoogleReservationSetted) {
      showSetupResourcesModel();
      return;
    }

    setShowGoogleLocationModal({ show: true, type: "get-started" });
  };

  const showSetupResourcesModel = () => {
    hideOnlineReservationModal();

    if (plan === "free") {
      showPurchaseModal();
      cancelSetupOnlineReservation();
      return;
    }

    if (plan === "premium") {
      // upgrade flow should be here
      cancelSetupOnlineReservation();
      showUpgradeModal();
      return;
    }

    showSetupResourcesModal();
  };

  const showSetupResourcesModal = () => {
    setForm({ ...form, selfBooking: true });
    setSetupResourcesModal(true);
  };
  const onCloseSetupResourcesModal = selfBooking => {
    setForm({ ...form, selfBooking: selfBooking || false, reserveWithGoogle: selfBooking || false });
    setSetupResourcesModal(false);
  };

  const onNextSetupResourcesModel = data => {
    onCloseSetupResourcesModal(!data.skipped);
    setResources(data);
    if (!data.skipped) {
      setConfirmationModal({ show: true, type: isOnlineReservationsModalFromGoogle ? "google" : "online" });
    }
  };

  const onValidChange = (day, time) => {
    let hours = [...form.hours];
    if (time.begin) {
      hours[week.indexOf(day)].begin = time.begin;
    }
    if (time.end) {
      hours[week.indexOf(day)].end = time.end;
    }
    if (time.closed !== undefined) {
      hours[week.indexOf(day)].closed = time.closed;
    }
    setForm({ ...form, hours: hours });
  };

  const isRestaurantReservations = () => {
    return getReservationsType() === RESERVATIONS_TYPES.RESERVATIONS;
  };

  const showWebsiteInstructionsModal = () => {
    setIsShowWebsiteInstructionsModal(true);
  };

  const onCloseWebsiteInstuctionsModal = () => {
    setIsShowWebsiteInstructionsModal(false);
  };

  const handleAppearanceModalClose = async data => {
    const snippet = data?.snippet;
    if (snippet && squareInfo?.squareOnlineSites?.length) {
      try {
        toastr.success("Snippet create in progress...");
        setEditSnippetModalConfig(null);
        await createSnippet({ snippet });
        toastr.success("Snippet was successfully created");
      } catch (err) {
        toastr.error(err.error || "There was an issue during snippet create, please contact support");
      }
    } else {
      if (snippet?.content) {
        setSnippetContent(snippet.content);
        showWebsiteInstructionsModal();
        setEditSnippetModalConfig(null);
        return;
      }
    }

    setEditSnippetModalConfig(null);
  };

  const validateWindow = () => {
    const values = getValues();
    const sv = {};
    sv[values.leadTimeUnits] = values.leadTimeValue;
    const ev = {};
    ev[values.scheduleWindowUnits] = values.scheduleWindowValue;
    let start = DateTime.now().plus(sv);
    let end = DateTime.now().plus(ev);
    return start < end || "Lead time must be before schedule window.";
  };

  const validateCoverFlowLimit = () => {
    const values = getValues();
    if (parseInt(values.coverFlowLimit, 10) < parseInt(maxResourcesCount, 10)) {
      return `Cover flow limit must be at least ${maxResourcesCount} or no one will be able to book your ${maxResourcesCount} top`;
    } else {
      return true;
    }
  };

  const showUpgradeErrorModal = (code = true) => {
    setUpgradeErrorModal(code);
  };

  const onCloseUpgradeErrorModal = () => {
    setUpgradeErrorModal(false);
  };

  const onCloseUpgradeModal = () => {
    setUpgradeModal(false);
  };

  const onClosePurchaseModal = () => {
    setPurchaseModal(false);
  };

  const onSquareToPremiunUpgrade = async () => {
    const result = await upgradePremiumToPro();
    onCloseUpgradeModal();
    if (result) {
      if (result.upgraded) {
        showSetupResourcesModal();
        return;
      }
      if (result.error) {
        const code = result.code;
        if (code === STRIPE_UPGRADE_ERROR.ORIGIN_IOS || code === STRIPE_UPGRADE_ERROR.WRONG_BILLING_ADDRESS) {
          showUpgradeErrorModal(code);
          return;
        }
        toastr.error(result.error);
        return;
      }
    } else {
      toastr.error("Something went wrong");
    }
  };

  const showIOSWarning = () => {
    return onlineReservations && account.plan === "premium" && account.origin === "ios";
  };

  const showLetsGetStarted = () => {
    if (account.plan === "premium" && account.origin === "ios") {
      return false;
    } else {
      return onlineReservations;
    }
  };

  const maxPartySizeChanged = size => {
    setMaxResourcesCount(size);
  };

  useEffect(() => {
    const sparams = new Proxy(params, {
      get: (searchParams, prop) => searchParams.get(prop)
    });
    if (sparams.reserveWithGoogle === "true") {
      return;
    }
    if (form.reserveWithGoogle && form.selfBooking) {
      if (!business) return;
      const address = business?.address || {};

      const isAddressForGoogleReservationSetted = Boolean(
        address.address1 &&
        address.state &&
        address.city &&
        address.postal &&
        address.country &&
        address.geo &&
        address.geo?.lat &&
        address.geo?.lng
      );

      if (!isAddressForGoogleReservationSetted) {
        setShowGoogleLocationModal({ show: true, type: "reserve-with-google" });
      }
    }
  }, [form.reserveWithGoogle]);

  useEffect(() => {
    if (!loading && !squareLoading) {
      const sparams = new Proxy(params, {
        get: (searchParams, prop) => searchParams.get(prop)
      });
      if (sparams.reserveWithGoogle === "true") {
        if (form.reserveWithGoogle) {
          return;
        }
        const isAddressForGoogleReservationSetted = checkIfAddressForGoogleReservationSetted();

        if (isAddressForGoogleReservationSetted && form.selfBooking) {
          setForm({ ...form, reserveWithGoogle: true });
          const saveBtn = document.getElementById("reservations-settings-save-btn");
          saveBtn.click();
          setCustomSettingsUpdatedMessage({
            content:
              "Reserve with Google is now turned on. It may take up to 48 hours for the change to be reflected on Google.",
            header: "Congratulations!"
          });
          return;
        }
        if (form.selfBooking) {
          setForm({ ...form, reserveWithGoogle: true });
          return;
        }
        setForm({ ...form, selfBooking: true, reserveWithGoogle: true });
        showOnlineReservationsModal();
        setIsOnlineReservationsModalFromGoogle(true);
      }
    }
  }, [loading, squareLoading]);

  if (loading || squareLoading)
    return (
      <div className="loading-wrapper">
        <BeatLoader margin={2.5} size={20} color={"#026FFF"} />
      </div>
    );

  return (
    <>
      {confirmationModal.show && (
        <Modal
          dialogClassName="online-reservations-confirmation-modal"
          show={confirmationModal.show}
          onHide={() => {
            setConfirmationModal({ show: false, type: null });
          }}
        >
          <CustomButton
            className="online-reservations-confirmation-modal__close-btn"
            simple
            onClick={() => {
              setConfirmationModal({ show: false, type: null });
            }}
          >
            <i className="fa fa-times" />
          </CustomButton>
          {confirmationModal.type === "google" ? (
            <>
              <SquareHeader>Reserve with Google is now turned on!</SquareHeader>
              <div>
                <p className="text-content">
                  It may take google up to 48 hours to reflect this change on your Google my business listing.
                </p>
              </div>
            </>
          ) : (
            <>
              <SquareHeader>Online {getBookingTypeDisplayName(account?.type)} is now turned on!</SquareHeader>
              <div>
                <p className="text-content">
                  Use the {isRestaurantReservations() ? "reservations" : "booking"} page link in settings or a website
                  widget to allow customers to book from your website.
                </p>
              </div>
            </>
          )}

          <div className="d-flex justify-content-end gap-2">
            {confirmationModal.type === "google" ? (
              <>
                <Button
                  onClick={() => setConfirmationModal({ show: false, type: null })}
                  bsStyle="info"
                  className="btn-fill"
                  pullRight
                  type="button"
                >
                  OK
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => setConfirmationModal({ show: false, type: null })}
                  bsStyle="info"
                  className=""
                  pullRight
                  type="button"
                >
                  Done
                </Button>
                <Button
                  onClick={() => history.push("/admin/settings?page=widgets")}
                  bsStyle="info"
                  className=" btn-fill"
                  pullRight
                  type="button"
                >
                  Show me Widgets
                </Button>
              </>
            )}
          </div>
        </Modal>
      )}
      {showGoogleLocationModal.show && (
        <AddLocationForGoogleModal
          show={showGoogleLocationModal.show}
          type={showGoogleLocationModal.type}
          onHide={type => {
            if (type === "get-started") {
              cancelSetupOnlineReservation();
              setShowGoogleLocationModal(state => ({ ...state, show: false }));
              return;
            }
            setShowGoogleLocationModal(state => ({ ...state, show: false }));
            setForm({ ...form, reserveWithGoogle: false });
          }}
          onSubmit={type => {
            if (type === "get-started") {
              showSetupResourcesModel();
              setShowGoogleLocationModal(state => ({ ...state, show: false }));
              return;
            }

            setShowGoogleLocationModal(state => ({ ...state, show: false }));
            const saveBtn = document.getElementById("reservations-settings-save-btn");
            saveBtn.click();
          }}
          business={business}
        />
      )}
      {!!upgradeErrorModal && (
        <Modal dialogClassName="square" show={!!upgradeErrorModal} onHide={onCloseUpgradeErrorModal}>
          <div className="square__content">
            <SquareHeader>Online {getBookingTypeDisplayName(account?.type)}</SquareHeader>
            <StripeUpgradeErrorContent type={upgradeErrorModal} closeModal={onCloseUpgradeErrorModal} />
          </div>
        </Modal>
      )}
      {showDeleteMessage && (
        <SweetAlert
          showCancel
          title={deleteResourceMessage.title}
          cancelBtnText="No"
          confirmBtnText="Yes"
          onCancel={hideInfoAlert}
          onConfirm={onDeleteResource}
        >
          {deleteResourceMessage.message}
        </SweetAlert>
      )}
      {showDeleteServiceMessage && (
        <SweetAlert
          showCancel
          title={deleteServiceMessage.title}
          cancelBtnText="No"
          confirmBtnText="Yes"
          onCancel={() => setShowDeleteServiceMessage(false)}
          onConfirm={onDeleteService}
        >
          {deleteServiceMessage.message} It will also be removed from any {resourceNoun(true).toLowerCase()} that offers
          it.
        </SweetAlert>
      )}
      {showResourceModal && (
        <AddResource
          updateResource={onEditResource}
          resource={selectedResource}
          services={services}
          businessHours={form.hours}
          isAppointmentResource={showAppointmentsSetup()}
          appointmentResourceLabelSingular={form.appointmentResourceLabelSingular || ""}
          showResourceModal={showResourceModal}
          closeModal={onCloseModal}
          loading={loading}
          business={business}
        />
      )}
      {showServiceModal && (
        <AddService
          updateService={onEditService}
          service={selectedService}
          showServiceModal={showServiceModal}
          closeModal={onCloseServiceModal}
          loading={loading}
        />
      )}
      {showSpecificDatesModal && (
        <SpecificDateCalendar
          showSpecificDateModal={showSpecificDatesModal}
          settings={settings}
          business={business}
          // `resources` holds two different shapes: the restaurant path stores raw
          // Square data with no `id`, the appointments path stores `{...data, id}`.
          // Only the latter can back a resource picker.
          resources={showAppointmentsSettings() ? resources : []}
          closeModal={onCloseSpecificDatesModal}
        />
      )}
      {showLandingPageEditor && (
        // No fallback UI: the chunk resolves in the time it takes the modal to animate
        // in, and a spinner that flashes for one frame reads as a fault.
        <Suspense fallback={null}>
          <LandingPageEditor
            businessId={business.businessId}
            initialContent={form.landingPageContent}
            // The editor writes to Firestore itself; this only keeps the page's form in
            // step, so a later page-level Save re-writes what was actually stored rather
            // than the content the page was loaded with.
            onSave={content => setForm(prev => ({ ...prev, landingPageEnabled: true, landingPageContent: content }))}
            closeModal={() => setShowLandingPageEditor(false)}
          />
        </Suspense>
      )}
      {showIOSWarning() && (
        <Modal dialogClassName="square" show={onlineReservations} onHide={cancelSetupOnlineReservation}>
          <div className="square__content">
            <div className="step-wrapper">
              <SquareHeader>Online {getBookingTypeDisplayName(account?.type)} require Waitly Professional</SquareHeader>
              <div>
                <p className="text-content">
                  Since your Waitly Premium Subscription is through Apple, you'll need to upgrade to Waitly Professional
                  from your Apple Device.
                </p>
              </div>

              <div className="d-flex justify-content-end">
                <Button
                  onClick={cancelSetupOnlineReservation}
                  bsStyle="info"
                  className="square-modal__button square-modal__button--left"
                  pullRight
                  type="button"
                >
                  Skip for now
                </Button>
                <Button
                  onClick={notifySupportOfReservationsInterest}
                  bsStyle="info"
                  className="square-modal__button btn-fill"
                  pullRight
                  type="button"
                >
                  Learn More
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
      {showLetsGetStarted() && (
        <Modal dialogClassName="square" show={onlineReservations} onHide={cancelSetupOnlineReservation}>
          <CustomButton
            className="square-modal__close-btn"
            simple
            onClick={() => {
              cancelSetupOnlineReservation();
            }}
          >
            <i className="fa fa-times" />
          </CustomButton>
          <div className="square__content">
            <SquareHeader>
              {isOnlineReservationsModalFromGoogle
                ? "Let's turn on Reserve with Google"
                : `Let's set up online ${isRestaurantReservations() ? "reservations" : "bookings"}`}
            </SquareHeader>
            <div>
              <p className="text-content">
                {isOnlineReservationsModalFromGoogle
                  ? "You're just a few steps away from taking reservation from Google."
                  : `You're just a few steps away from accepting ${isRestaurantReservations() ? "reservations" : "bookings"} from your website.`}
              </p>
            </div>
            <div
              className={cx({
                "d-flex ": true,
                "mt-1 mb-1 justify-content-center": isMobile,
                "justify-content-end": !isMobile
              })}
            >
              <Button
                onClick={cancelSetupOnlineReservation}
                bsStyle="info"
                className="square-modal__button square-modal__button--left"
                pullRight
                type="button"
              >
                Cancel
              </Button>

              <Button
                onClick={showSetupAddressModal}
                bsStyle="info"
                className="square-modal__button btn-fill"
                pullRight
                type="button"
              >
                Get Started
              </Button>
            </div>
          </div>
        </Modal>
      )}
      {upgradeModal && (
        <Modal dialogClassName="square" show={upgradeModal} onHide={onCloseUpgradeModal}>
          <div className="square__content">
            <SquareHeader>Upgrade account to Waitly Professional</SquareHeader>
            <div className="upgrade-subtitle">Waitly Professional is required to take reservations online.</div>
            <UpgradeSquare
              isProfessional={true}
              exitPoint={"onlineReservations"}
              skipUpgrade={onCloseUpgradeModal}
              onUpgrade={onSquareToPremiunUpgrade}
              upgradeType={PREMIUM_TO_PRO}
            />
          </div>
        </Modal>
      )}
      {purchaseModal && (
        <Modal dialogClassName="square" show={purchaseModal} onHide={onClosePurchaseModal}>
          <div className="square__content">
            <SquareHeader>Activate 30 day free trial of Waitly Professional</SquareHeader>
            <div className="upgrade-subtitle">Waitly Professional is required to take reservations online.</div>
            <UpgradeSquare isProfessional={true} skipUpgrade={onClosePurchaseModal} exitPoint={"onlineReservations"} />
          </div>
        </Modal>
      )}
      {setupResourcesModal && (
        <Modal dialogClassName="square" show={setupResourcesModal} onHide={onCloseSetupResourcesModal}>
          <div className="square__content">
            <div className="step-wrapper">
              {resourcesLoading ? (
                <div className="loader-wrapper">
                  <BeatLoader margin={2.5} size={15} color={"#016BFF"} />
                </div>
              ) : (
                <SquareReservationSetup
                  goToNextStep={onNextSetupResourcesModel}
                  minPartySize={business.settings.reservations.minPartySize}
                  maxResourcesCount={maxResourcesCount}
                  businessId={business.businessId}
                  isLastStep
                  resources={resources}
                  reserveWithGoogle={form.reserveWithGoogle}
                  shoudReturnResorces={true}
                />
              )}
            </div>
          </div>
        </Modal>
      )}
      {editSnippetModalConfig && (
        <SquareAppearanceEditModal
          handleAppearanceModalClose={handleAppearanceModalClose}
          squareInfo={squareInfo}
          config={editSnippetModalConfig}
        />
      )}
      {isShowWebsiteInstructionsModal && (
        <WebsiteInstructionsModal
          show={isShowWebsiteInstructionsModal}
          user={user}
          close={onCloseWebsiteInstuctionsModal}
          snippetContent={snippetContent}
        />
      )}
      <SettingsWrapper
        title={`${getBookingTypeDisplayName(account?.type)} ${props.businessName ? " - " + props.businessName : ""}`}
        handleSubmit={handleSubmit(onSubmit)}
        buttonStyles={{ maxWidth: 830, marginTop: 20 }}
        submitBtnDisable={resourcesLoading}
        saveBtnId={"reservations-settings-save-btn"}
      >
        {showEnableReservationsToggle() && (
          <Row>
            <Col md={6}>
              <SettingsInput
                form={form}
                name={"enableReservations"}
                type={"switch"}
                enabled={true}
                errors={errors}
                value={form.enableReservations}
                label={getBookingTypeDisplayName(account?.type)}
                subtitle={`Ability to book ${isRestaurantReservations() ? "reservations" : "appointments"} using Waitly.`}
                register={null}
                onChangeValue={handleEnableReservationToggle}
              />
            </Col>
          </Row>
        )}
        {showSelfBookingToggle() && (
          <Row>
            <Col md={6}>
              <SettingsInput
                form={form}
                name={"selfBooking"}
                type={"switch"}
                enabled={form.enableReservations}
                errors={errors}
                value={form.selfBooking}
                label={`Online ${getBookingTypeDisplayName(account?.type)}`}
                subtitle={`Guests can book online`}
                register={null}
                onChangeValue={handleOnlineReservationToggle}
              />
            </Col>
          </Row>
        )}
        {showRwGSwitch() && (
          <Row>
            <Col md={6}>
              <SettingsInput
                form={form}
                name={"reserveWithGoogle"}
                type={"switch"}
                enabled={form.enableReservations}
                errors={errors}
                value={form.reserveWithGoogle}
                label={"Reserve with Google"}
                subtitle={`Allow guests to book through Google`}
                register={null}
                onChangeValue={handleReserveWithGoogleToggle}
              />
            </Col>
          </Row>
        )}
        {isNotTL() && (isRestaurantReservations() || showAppointmentsSettings()) && form.selfBooking && (
          <Row>
            <Col md={12}>
              <FormGroup className="flex settings-form-group" style={{ alignItems: "center" }}>
                <div className="settings-label-wrapper">
                  <ControlLabel style={{ color: !form.selfBooking && "#CAC9C9" }} className="settings-label">
                    {getBookingTypeDisplayName(account?.type)} Page
                  </ControlLabel>
                  <ControlLabel
                    style={{ color: !form.selfBooking && "#CAC9C9", fontWeight: "600", paddingTop: "15px" }}
                    className="settings-subtitle"
                  >
                    Guest can book {isRestaurantReservations() ? "a reservation" : "an appointment"} at this link
                  </ControlLabel>
                  <div className="flex">
                    <div className="settings-waitlist-link-wrapper">
                      <a
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          cursor: !form.selfBooking && "default",
                          pointerEvents: !form.selfBooking && "none",
                          color: !form.selfBooking && "#CAC9C9"
                        }}
                        href={bookLink}
                      >
                        {bookLink}
                      </a>
                    </div>
                    {!bookLinkCopied && (
                      <i
                        onClick={onCopyLink}
                        style={{ color: !form.selfBooking && "#CAC9C9", pointerEvents: !form.selfBooking && "none" }}
                        className="mdi mdi-24px mdi-clipboard-text"
                      />
                    )}
                    {bookLinkCopied && <i style={{ color: "green" }} className="mdi mdi-24px mdi-clipboard-check" />}
                  </div>
                </div>
              </FormGroup>
            </Col>
          </Row>
        )}
        {form.enableReservations && (
          <Row>
            <Col>
              {isRestaurantReservations() && isNotTL() ? (
                <div className="ml-4">
                  {loading ? (
                    <div className="loader-wrapper">
                      <BeatLoader margin={2.5} size={15} color={"#016BFF"} />
                    </div>
                  ) : (
                    <SquareReservationSetup
                      goToNextStep={() => {
                        if (customSettingsUpdatedMessage) {
                          toastr.success(customSettingsUpdatedMessage.content, customSettingsUpdatedMessage.header);
                          setCustomSettingsUpdatedMessage(null);
                        } else {
                          toastr.success("Settings updated");
                          if (form.reserveWithGoogle) {
                            toastr.success("It may take up to 48 hours for these changes to reflected on Google.");
                          }
                        }
                      }}
                      reserveWithGoogle={form.reserveWithGoogle}
                      maxResourcesCount={maxResourcesCount}
                      resources={resources}
                      isInModal={false}
                      minPartySize={business.settings.reservations.minPartySize}
                      shouldSubmit={shouldSubmitResources}
                      maxPartySizeChanged={maxPartySizeChanged}
                      businessId={business.businessId}
                      setShouldSubmit={setShouldSubmitResources}
                      setLoading={setResourcesLoading}
                    />
                  )}
                </div>
              ) : (
                <div style={{ maxWidth: "100%" }} className="ml-2">
                  {isRestaurantReservations() ? (
                    <FormGroup className="flex settings-form-group" style={{ alignItems: "center", maxWidth: "98%" }}>
                      <div className="settings-label-wrapper">
                        <ControlLabel className="settings-label">Table Capacity</ControlLabel>
                        <ControlLabel className="settings-subtitle">Define tables that can be booked.</ControlLabel>
                      </div>
                      <Button onClick={onAddResource} bsStyle="info" fill pullRight type="button">
                        Add Table Capacity
                      </Button>
                    </FormGroup>
                  ) : (
                    <>
                      {showAppointmentsSetup() && (
                        <>
                          <FormGroup
                            className="flex settings-form-group"
                            style={{ alignItems: "center", maxWidth: "98%" }}
                          >
                            <div className="settings-label-wrapper">
                              <ControlLabel className="settings-label">Services</ControlLabel>
                              <ControlLabel className="settings-subtitle">
                                The services offered by your {resourceNoun(true)}
                              </ControlLabel>
                            </div>
                            <Button onClick={onAddService} bsStyle="info" fill pullRight type="button">
                              Add Service
                            </Button>
                          </FormGroup>
                          <RegularTable
                            rows={onGetServicesForTable()}
                            editAction={onSelectServiceForEdit}
                            deleteAction={onShowDeleteServiceMessage}
                            tableHeaders={SERVICE_HEADERS}
                            actions={SERVICE_ACTIONS}
                            hideAction={onHideDeleteAction}
                            hideColumn={onHideServiceColumn}
                            emptyRowsMessage="No services yet."
                          />
                        </>
                      )}
                      <FormGroup className="flex settings-form-group" style={{ alignItems: "center", maxWidth: "98%" }}>
                        <div className="settings-label-wrapper">
                          <ControlLabel className="settings-label">{resourceNoun(true)}</ControlLabel>
                          <ControlLabel className="settings-subtitle">
                            What your guests are booking time with.
                          </ControlLabel>
                        </div>
                        <Button onClick={onAddResource} bsStyle="info" fill pullRight type="button">
                          Add {resourceNoun()}
                        </Button>
                      </FormGroup>
                      {resources?.length > 0 && (
                        <RegularTable
                          rows={onGetResourcesForTable()}
                          editAction={onSelectResourceForEdit}
                          deleteAction={onShowDeleteResourceMessage}
                          tableHeaders={showAppointmentsSetup() ? APPOINTMENT_HEADERS : HEADERS}
                          actions={ACTIONS}
                          hideAction={onHideDeleteAction}
                        />
                      )}
                      {showAppointmentsSettings() && (
                        <>
                          <FormGroup
                            className="flex settings-form-group"
                            style={{ alignItems: "center", marginTop: "20px" }}
                          >
                            <div className="settings-label-wrapper">
                              <ControlLabel className="settings-label">Resource labels</ControlLabel>
                              <ControlLabel className="settings-subtitle">
                                What do you call your resources?
                              </ControlLabel>
                            </div>
                          </FormGroup>
                          <div
                            className="flex settings-input-wrapper"
                            style={{ marginLeft: "15px", flexDirection: "column" }}
                          >
                            <div className="flex" style={{ flexDirection: "row", gap: "10px", marginBottom: "5px" }}>
                              <input
                                className="form-control settings-input"
                                type="text"
                                maxLength={30}
                                placeholder="Stylist"
                                value={form.appointmentResourceLabelSingular}
                                onChange={e => setForm({ ...form, appointmentResourceLabelSingular: e.target.value })}
                                disabled={!form.enableReservations}
                                style={{ width: "125px", borderColor: "#979797" }}
                              />
                              <input
                                className="form-control settings-input"
                                type="text"
                                maxLength={30}
                                placeholder="Stylists"
                                value={form.appointmentResourceLabelPlural}
                                onChange={e => setForm({ ...form, appointmentResourceLabelPlural: e.target.value })}
                                disabled={!form.enableReservations}
                                style={{ width: "125px", borderColor: "#979797" }}
                              />
                            </div>
                            <HelpBlock style={{ color: "#626262", marginBottom: "15px" }}>
                              Singular and plural, used wherever guests pick one — e.g. Stylist, Room, Lane, Chair.
                              Leave blank and we&apos;ll keep the wording generic.
                            </HelpBlock>
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
            </Col>
          </Row>
        )}
        {showOnlineReservation() && (
          <Row>
            <Col md={6}>
              <SettingsInput
                form={form}
                name={"selfBooking"}
                type={"switch"}
                enabled={form.enableReservations}
                errors={errors}
                value={form.allowBookingLargerResource}
                label={"Book larger tables"}
                subtitle={`Guests can book can book a larger table if there are no tables available for their party size`}
                register={null}
                onChangeValue={handleBookingLargerResourceToggle}
              />
            </Col>
          </Row>
        )}
        {/* Lead time and booking window are shared between reservations and
            appointments (plan §0); cover flow limit stays restaurant-only. */}
        {isNotTL() && (isRestaurantReservations() || showAppointmentsSettings()) && (
          <>
            {isRestaurantReservations() && (
              <>
                <Row>
                  <Col md={12}>
                    <FormGroup className="flex settings-form-group" style={{ alignItems: "center" }}>
                      <div className="settings-label-wrapper">
                        <ControlLabel
                          style={{ color: !form.enableReservations && "#CAC9C9" }}
                          className="settings-label"
                        >
                          Cover flow limit
                        </ControlLabel>
                        <ControlLabel
                          style={{ color: !form.enableReservations && "#CAC9C9" }}
                          className="settings-subtitle"
                        >
                          Further limit the number of covers that can be booked in a 15 minute period
                        </ControlLabel>
                      </div>
                    </FormGroup>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <div className="flex settings-input-wrapper" style={{ marginLeft: "15px", flexDirection: "row" }}>
                      <select
                        name="coverFlowLimit"
                        className="form-control settings-input"
                        defaultValue={form.coverFlowLimit}
                        ref={register({ validate: validateCoverFlowLimit })}
                        disabled={!form.enableReservations}
                        style={{ width: "100px", borderColor: "#989898" }}
                      >
                        {coverLimitOptions.map(({ value, label }, index) => (
                          <option value={value} key={"coverLimt" + index}>
                            {label}
                          </option>
                        ))}
                      </select>
                      <span style={{ marginLeft: "10px", color: "#626262", marginTop: "10px" }}>
                        {" "}
                        covers every 15 minutes
                      </span>
                    </div>
                    {errors && errors["coverFlowLimit"] && errors.coverFlowLimit.type === "validate" && (
                      <HelpBlock style={{ color: "red", marginLeft: "15px" }}>
                        {errors.coverFlowLimit.message}
                      </HelpBlock>
                    )}
                  </Col>
                </Row>
              </>
            )}
            <Row>
              <Col md={12}>
                <FormGroup className="flex settings-form-group" style={{ alignItems: "center" }}>
                  <div className="settings-label-wrapper">
                    <ControlLabel style={{ color: !form.enableReservations && "#CAC9C9" }} className="settings-label">
                      Booking lead time
                    </ControlLabel>
                    <ControlLabel
                      style={{ color: !form.enableReservations && "#CAC9C9" }}
                      className="settings-subtitle"
                    >
                      How much advanced noticed do you require before a guest can book
                    </ControlLabel>
                  </div>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <div className="flex settings-input-wrapper" style={{ marginLeft: "15px", flexDirection: "row" }}>
                  <input
                    name="leadTimeValue"
                    style={{ width: "70px", marginRight: "20px", borderColor: "#979797" }}
                    className="form-control settings-input"
                    type={"number"}
                    ref={register({ required: true, min: 1, max: 180, validate: validateWindow })}
                    defaultValue={form.leadTimeValue}
                    disabled={!form.enableReservations}
                  />
                  <select
                    name="leadTimeUnits"
                    className="form-control settings-input"
                    defaultValue={form.leadTimeUnits}
                    ref={register()}
                    disabled={!form.enableReservations}
                    style={{ width: "100px", borderColor: "#979797" }}
                  >
                    <option value="minutes">Minutes</option>
                    <option value="hours">Hours</option>
                    <option value="days">Days</option>
                    <option value="months">Months</option>
                  </select>
                </div>
                {errors && errors["leadTimeValue"] && errors.leadTimeValue.type === "validate" && (
                  <HelpBlock style={{ color: "red", marginLeft: "15px" }}>{errors.leadTimeValue.message}</HelpBlock>
                )}
                {errors && errors["leadTimeValue"] && errors.leadTimeValue.type === "required" && (
                  <HelpBlock style={{ color: "red", marginLeft: "15px" }}>Lead time is required.</HelpBlock>
                )}
                {errors && errors["leadTimeValue"] && errors.leadTimeValue.type === "min" && (
                  <HelpBlock style={{ color: "red", marginLeft: "15px" }}>Value must be at least 1.</HelpBlock>
                )}
                {errors && errors["leadTimeValue"] && errors.leadTimeValue.type === "max" && (
                  <HelpBlock style={{ color: "red", marginLeft: "15px" }}>The max value is 180.</HelpBlock>
                )}
              </Col>
            </Row>
            <Row>
              <Col md={11}>
                <FormGroup className="flex settings-form-group">
                  <div className="settings-label-wrapper">
                    <ControlLabel style={{ color: !form.enableReservations && "#CAC9C9" }} className="settings-label">
                      Booking window
                    </ControlLabel>
                    <ControlLabel
                      style={{ color: !form.enableReservations && "#CAC9C9" }}
                      className="settings-subtitle"
                    >
                      How far in advance can guests book
                    </ControlLabel>
                    {form.reserveWithGoogle && (
                      <ControlLabel className="settings-subtitle" style={{ fontStyle: "italic" }}>
                        Note: Booking window for Google should be in range from 7 days to 30 days. Otherwise, it will be
                        set to range bounds.
                      </ControlLabel>
                    )}
                  </div>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={11}>
                <div className="flex settings-input-wrapper" style={{ marginLeft: "15px", flexDirection: "row" }}>
                  <input
                    name="scheduleWindowValue"
                    style={{ width: "70px", marginRight: "20px", borderColor: "#979797" }}
                    className="form-control settings-input"
                    type={"number"}
                    ref={register({ required: true, max: 180, min: 1 })}
                    defaultValue={form.scheduleWindowValue}
                    disabled={!form.enableReservations}
                  />
                  <select
                    name="scheduleWindowUnits"
                    className="form-control settings-input"
                    defaultValue={form.scheduleWindowUnits}
                    ref={register()}
                    disabled={!form.enableReservations}
                    style={{ width: "100px", borderColor: "#979797" }}
                  >
                    <option value="minutes">Minutes</option>
                    <option value="hours">Hours</option>
                    <option value="days">Days</option>
                    <option value="months">Months</option>
                  </select>
                </div>
                {errors && errors["scheduleWindowValue"] && errors.scheduleWindowValue.type === "required" && (
                  <HelpBlock style={{ color: "red", marginLeft: "15px" }}>Booking window is required.</HelpBlock>
                )}
                {errors && errors["scheduleWindowValue"] && errors.scheduleWindowValue.type === "min" && (
                  <HelpBlock style={{ color: "red", marginLeft: "15px" }}>Value must be at least 1.</HelpBlock>
                )}
                {errors && errors["scheduleWindowValue"] && errors.scheduleWindowValue.type === "max" && (
                  <HelpBlock style={{ color: "red", marginLeft: "15px" }}>The max value is 180.</HelpBlock>
                )}
              </Col>
            </Row>
          </>
        )}
        {showAppointmentsSettings() && (
          <>
            <Row>
              <Col md={11}>
                <FormGroup className="flex settings-form-group">
                  <div className="settings-label-wrapper">
                    <ControlLabel className="settings-label">Booking flow</ControlLabel>
                    <ControlLabel className="settings-subtitle">
                      How guests move through the appointment booking page.
                    </ControlLabel>
                  </div>
                </FormGroup>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <div className="flex settings-input-wrapper" style={{ marginLeft: "15px", flexDirection: "column" }}>
                  {showStepOrderSelect() && (
                    <>
                      <ControlLabel className="settings-subtitle">Guests choose first</ControlLabel>
                      <select
                        className="form-control settings-input"
                        value={form.appointmentStepOrder}
                        onChange={e => setForm({ ...form, appointmentStepOrder: e.target.value })}
                        disabled={!form.enableReservations}
                        style={{ width: "260px", borderColor: "#979797", marginBottom: "15px" }}
                      >
                        <option value="service-first">{`A service, then ${aResourceNoun()}`}</option>
                        <option value="resource-first">{`${resourceNoun()}, then a service`}</option>
                      </select>
                    </>
                  )}

                  <ControlLabel className="settings-subtitle">Time slots every</ControlLabel>
                  <select
                    className="form-control settings-input"
                    value={form.appointmentSlotInterval}
                    onChange={e => setForm({ ...form, appointmentSlotInterval: e.target.value })}
                    disabled={!form.enableReservations}
                    style={{ width: "260px", borderColor: "#979797" }}
                  >
                    {[5, 10, 15, 20, 30, 60].map(minutes => (
                      <option key={minutes} value={minutes}>
                        {minutes} minutes
                      </option>
                    ))}
                  </select>
                </div>
              </Col>
            </Row>
            {form.appointmentStepOrder === "service-first" && (
              <Row>
                <Col md={6}>
                  <SettingsInput
                    form={form}
                    name={"appointmentShowFirstAvailable"}
                    type={"switch"}
                    enabled={form.enableReservations}
                    value={form.appointmentShowFirstAvailable}
                    label={"Offer First Available"}
                    subtitle={"Let guests skip choosing and take the soonest opening"}
                    register={null}
                    onChangeValue={toggleChecked}
                  />
                </Col>
              </Row>
            )}
            <Row>
              <Col md={6}>
                <SettingsInput
                  form={form}
                  name={"appointmentShowResourcePhotos"}
                  type={"switch"}
                  enabled={form.enableReservations}
                  value={form.appointmentShowResourcePhotos}
                  label={`Show ${resourceNoun(false).toLowerCase()} photos`}
                  subtitle={`Display photos when guests pick ${aResourceNoun()}`}
                  register={null}
                  onChangeValue={toggleChecked}
                />
              </Col>
            </Row>
          </>
        )}
        {showHideMapToggle() && (
          <Row>
            <Col md={6}>
              <SettingsInput
                form={form}
                name={"hideMap"}
                type={"switch"}
                enabled={form.enableReservations}
                errors={errors}
                value={form.hideMap}
                label={"Hide Map"}
                subtitle={"Hide the location map on your online booking page"}
                register={null}
                onChangeValue={toggleChecked}
              />
            </Col>
          </Row>
        )}
        {showAppointmentsSettings() && (
          <>
            <Row>
              <Col md={6}>
                <SettingsInput
                  form={form}
                  name={"landingPageEnabled"}
                  type={"switch"}
                  enabled={form.enableReservations}
                  value={form.landingPageEnabled}
                  label={"Landing page"}
                  subtitle={"Open your booking page with a page you write, and a Book now button"}
                  register={null}
                  onChangeValue={toggleChecked}
                />
              </Col>
            </Row>
            {/* Below the switch on its own row, matching the "Set Hours" button further
                down — and only once the switch is on: an edit button for a page that
                will not be shown is an invitation to write something nobody reads. */}
            {form.landingPageEnabled && (
              <Row>
                <Col md={11}>
                  <Button
                    onClick={() => setShowLandingPageEditor(true)}
                    bsStyle="info"
                    disabled={!form.enableReservations}
                    style={{ marginLeft: "20px", fontWeight: 600 }}
                    type="button"
                  >
                    Edit Landing Page
                  </Button>
                </Col>
              </Row>
            )}
          </>
        )}
        {isNotTL() && (isRestaurantReservations() || showAppointmentsSettings()) && form.selfBooking && (
          <>
            <Row>
              <Col md={12}>
                <FormGroup className="flex settings-form-group">
                  <div className="settings-label-wrapper">
                    <ControlLabel style={{ color: !form.selfBooking && "#CAC9C9" }} className="settings-label">
                      {getBookingTypeDisplayName(account?.type)} Policy
                    </ControlLabel>
                    <ControlLabel style={{ color: !form.selfBooking && "#CAC9C9" }} className="settings-subtitle">
                      Add a {isRestaurantReservations() ? "reservation" : "booking"} policy that will be displayed to
                      guests when booking.
                    </ControlLabel>
                  </div>
                </FormGroup>
                <div className="flex settings-input-wrapper">
                  <textarea
                    name="reservationPolicy"
                    className="form-control settings-input"
                    disabled={!form.enableReservations}
                    maxLength={250}
                    value={form.reservationPolicy}
                    onChange={e => setForm({ ...form, reservationPolicy: e.target.value })}
                    placeholder="Enter your reservation policy here..."
                    style={{ borderColor: "#979797", resize: "none", width: "350px", height: "100px" }}
                  />
                </div>
                <div style={{ marginTop: "5px", fontSize: "12px", color: "#626262" }}>
                  {form.reservationPolicy.length}/250 characters
                </div>
              </Col>
            </Row>
          </>
        )}
        <>
          <Row>
            <Col md={11}>
              <FormGroup className="flex settings-form-group" style={{ alignItems: "center" }}>
                <div className="settings-label-wrapper">
                  <ControlLabel style={{ color: !form.enableReservations && "#CAC9C9" }} className="settings-label">
                    Business booking hours
                  </ControlLabel>
                  <ControlLabel style={{ color: !form.enableReservations && "#CAC9C9" }} className="settings-subtitle">
                    The hours that can be booked for {isRestaurantReservations() ? "reservations" : "appointments"}.
                  </ControlLabel>
                </div>
              </FormGroup>
            </Col>
          </Row>

          <Row>
            {form.hours.map((hour, index) => (
              <WaitlistSettingsHour
                key={index}
                time={hour}
                value={!hour.closed}
                disabled={!form.enableReservations}
                day={week[index]}
                onValidChange={onValidChange}
              />
            ))}
          </Row>
        </>
        {isNotTL() && (isRestaurantReservations() || showAppointmentsSettings()) && (
          <>
            <Row>
              <Col md={11}>
                <FormGroup className="flex settings-form-group">
                  <div className="settings-label-wrapper">
                    <ControlLabel style={{ color: !form.enableReservations && "#CAC9C9" }} className="settings-label">
                      Holidays and Special Hours
                    </ControlLabel>
                    <ControlLabel
                      style={{ color: !form.enableReservations && "#CAC9C9" }}
                      className="settings-subtitle"
                    >
                      Override hours for specific dates such as holidays
                      {showAppointmentsSettings()
                        ? ", for the whole business or for one " + resourceNoun().toLowerCase() + "."
                        : "."}
                    </ControlLabel>
                  </div>
                </FormGroup>
              </Col>
            </Row>

            <Row style={{ marginBottom: "50px" }}>
              <Col md={11}>
                <Button
                  onClick={onSpecificDates}
                  bsStyle="info"
                  disabled={!form.enableReservations}
                  style={{ marginLeft: "20px", fontWeight: 600 }}
                  type="button"
                >
                  Set Hours
                </Button>
              </Col>
            </Row>
          </>
        )}
      </SettingsWrapper>
    </>
  );
};

export default ReservationSettings;
